import React, { useRef, useState } from 'react';
import axios from 'axios';
import {
    X,
    Download,
    Printer,
    Mail,
    CheckCircle
} from 'lucide-react';
import "../css/StudentBillPDF.css";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

function HostelStudentBillPDF({
    student,
    billDetails,
    onClose,
    paymentMethod,
    receiptNo = `CRPB${new Date().getFullYear()}${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`,
    receiptDate = new Date().toLocaleDateString('en-GB')
}) {
    const pdfRef = useRef(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // Helper function to ensure receipt exists in DB first
    const ensureReceiptExists = async () => {
        try {
            // First check if receipt already exists
            const checkResponse = await axios.get(`https://payroll-api.uniums.com/api/hostel/receipt/${receiptNo}`);

            // If we get here, receipt exists, return it
            return checkResponse.data.receipt;
        } catch (error) {
            // If 404, receipt doesn't exist, create it
            if (error.response && error.response.status === 404) {
                // Create new receipt
                const createData = {
                    receiptNo,
                    studentId: student._id,
                    rollNo: student.rollNo,
                    amount: billDetails.amount,
                    title: billDetails.title,
                    paymentMethod,
                    remarks: billDetails.remarks,
                    receiptDate: new Date(receiptDate)
                };

                const createResponse = await axios.post('https://payroll-api.uniums.com/api/hostel/create-receipt', createData);
                return createResponse.data.receipt;
            } else {
                // Other error
                throw error;
            }
        }
    };

    // Function to log action to the database
    const logReceiptAction = async (actionType) => {
        await axios.post('https://payroll-api.uniums.com/api/hostel/log-receipt-action', {
            receiptNo,
            actionType
        });
    };

    const handlePrint = async () => {
        setIsProcessing(true);
        setErrorMessage('');

        try {
            // Make sure receipt exists in database
            await ensureReceiptExists();

            // Log the print action
            await logReceiptAction('print');

            const printContent = document.getElementById('bill-pdf-content');
            const WinPrint = window.open('', '', 'width=900,height=650');

            WinPrint.document.write(`
              <html>
                <head>
                  <title>College Fee Receipt</title>
                  <style>
                    body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
                    .bill-document { width: 100%; max-width: 100%; margin: 0 auto; padding: 20px; }
                    .bill-header { text-align: center; margin-bottom: 20px; }
                    .bill-title { margin: 10px 0; font-size: 18px; font-weight: bold; text-align: center; }
                    .student-copy { text-align: right; font-style: italic; margin-bottom: 10px; }
                    
                    /* Critical table styles for print */
                    table { width: 100%; border-collapse: collapse; margin: 15px 0; table-layout: fixed; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; word-wrap: break-word; }
                    
                    /* Ensure specific column widths are maintained */
                    .bill-items-table th:nth-child(1), .bill-items-table td:nth-child(1) { width: 10%; }
                    .bill-items-table th:nth-child(2), .bill-items-table td:nth-child(2) { width: 60%; }
                    .bill-items-table th:nth-child(3), .bill-items-table td:nth-child(3) { width: 30%; }
                    
                    .bill-footer { text-align: center; font-size: 12px; margin-top: 30px; color: #666; }
                    .bill-amount { font-weight: bold; }
                    .bill-total { font-weight: bold; text-align: right; }
                    
                    /* Ensure footer table is fully visible */
                    .bill-footer-table { page-break-inside: avoid; width: 100%; }
                    
                    @media print {
                      body { width: 100%; }
                      .bill-document { width: 100%; max-width: 100%; }
                      table { page-break-inside: avoid; width: 100%; }
                      tr { page-break-inside: avoid; }
                    }
                  </style>
                </head>
                <body>
                  ${printContent.innerHTML}
                </body>
              </html>
            `);

            WinPrint.document.close();
            WinPrint.focus();
            setTimeout(() => {
                WinPrint.print();
                WinPrint.close();
            }, 1000);
            onClose();
        } catch (error) {
            console.error("Print error:", error);
            setErrorMessage('Failed to print receipt. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDownload = async () => {
        setIsProcessing(true);
        setErrorMessage('');

        try {
            // Make sure receipt exists in database
            await ensureReceiptExists();

            // Log the download action
            await logReceiptAction('download');

            const input = document.getElementById('bill-pdf-content');
            const originalOverflow = document.body.style.overflow;

            // Temporarily modify styles to ensure full content is captured
            document.body.style.overflow = 'visible'; // Prevent scrollbars from affecting capture

            // Store original styles
            const originalStyles = {
                width: input.style.width,
                height: input.style.height,
                overflow: input.style.overflow,
                position: input.style.position
            };

            // Modify styles to ensure full content is captured
            input.style.width = '800px';
            input.style.height = 'auto';
            input.style.overflow = 'visible';

            html2canvas(input, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                scrollX: 0,
                scrollY: 0,
                width: 800,
                height: input.scrollHeight,
                windowWidth: 800,
                windowHeight: input.scrollHeight,
                // Ensure all content is captured, including overflow
                onclone: (clonedDoc) => {
                    const clonedContent = clonedDoc.getElementById('bill-pdf-content');
                    if (clonedContent) {
                        clonedContent.style.width = '800px';
                        clonedContent.style.height = 'auto';
                        clonedContent.style.overflow = 'visible';

                        // Ensure all tables use fixed layout and proper width
                        const tables = clonedContent.querySelectorAll('table');
                        tables.forEach(table => {
                            table.style.width = '100%';
                            table.style.tableLayout = 'fixed';
                            table.style.pageBreakInside = 'avoid';
                        });

                        // Ensure item table columns have proper width
                        const itemsTable = clonedContent.querySelector('.bill-items-table');
                        if (itemsTable) {
                            const cols = itemsTable.querySelectorAll('col');
                            if (cols.length === 3) {
                                cols[0].style.width = '10%';
                                cols[1].style.width = '60%';
                                cols[2].style.width = '30%';
                            }
                        }
                    }
                }
            }).then((canvas) => {
                // Restore original styles
                input.style.width = originalStyles.width;
                input.style.height = originalStyles.height;
                input.style.overflow = originalStyles.overflow;
                input.style.position = originalStyles.position;
                document.body.style.overflow = originalOverflow;

                const imgData = canvas.toDataURL('image/png');
                const pdf = new jsPDF({
                    orientation: 'portrait',
                    unit: 'mm',
                    format: 'a4',
                    compress: true
                });

                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = pdf.internal.pageSize.getHeight();
                const imgWidth = canvas.width;
                const imgHeight = canvas.height;

                // Calculate appropriate scaling
                const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
                const imgX = (pdfWidth - imgWidth * ratio) / 2;

                // If the image is too tall for one page, split it across multiple pages
                const totalPages = Math.ceil(imgHeight * ratio / (pdfHeight - 20)); // 20mm margin for safety

                if (totalPages > 1) {
                    let heightLeft = imgHeight;
                    let position = 0;

                    for (let i = 0; i < totalPages; i++) {
                        // Add new page if not the first page
                        if (i > 0) {
                            pdf.addPage();
                        }

                        const heightToPrint = Math.min(pdfHeight * imgHeight / (imgHeight * ratio), heightLeft);

                        pdf.addImage(
                            imgData,
                            'PNG',
                            imgX,
                            i === 0 ? 0 : -position,
                            imgWidth * ratio,
                            imgHeight * ratio,
                            undefined,
                            'FAST'
                        );

                        heightLeft -= heightToPrint;
                        position += pdfHeight;
                    }
                } else {
                    // If content fits on one page
                    pdf.addImage(imgData, 'PNG', imgX, 0, imgWidth * ratio, imgHeight * ratio);
                }

                pdf.save(`${student?.name || 'Student'}_Receipt_${receiptNo}.pdf`);
            });
            onClose();
        } catch (error) {
            console.error("Download error:", error);
            setErrorMessage('Failed to download receipt. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSendEmail = async () => {
        setIsProcessing(true);
        setErrorMessage('');

        try {
            // Make sure receipt exists in database
            await ensureReceiptExists();

            // Log the email action
            await logReceiptAction('email');

            alert(`Email with receipt would be sent to ${student.email}`);

        } catch (error) {
            console.error("Email error:", error);
            setErrorMessage('Failed to send email. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="bill-pdf-overlay">
            <div className="bill-pdf-container">
                <div className="bill-pdf-header">
                    <h2>Bill Preview</h2>
                    <button className="bill-close-button" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="bill-pdf-actions">
                    <button
                        className="bill-action-button"
                        onClick={handleDownload}
                        disabled={isProcessing}
                    >
                        <Download size={16} />
                        {isProcessing ? 'Processing...' : 'Download PDF'}
                    </button>
                    <button
                        className="bill-action-button"
                        onClick={handlePrint}
                        disabled={isProcessing}
                    >
                        <Printer size={16} />
                        {isProcessing ? 'Processing...' : 'Print Receipt'}
                    </button>
                    <button
                        className="bill-action-button"
                        onClick={handleSendEmail}
                        disabled={isProcessing}
                    >
                        <Mail size={16} />
                        {isProcessing ? 'Processing...' : 'Email to Student'}
                    </button>
                </div>

                {errorMessage && (
                    <div className="bill-error-message">
                        {errorMessage}
                    </div>
                )}

                <div className="bill-pdf-content" id="bill-pdf-content" ref={pdfRef}>
                    <div className="bill-document">
                        <div className="bill-header">
                            <h1>Mayurbhanj College of Accountancy and Management</h1>
                            <p>{student?.department || "Department"} Student</p>
                            <p>Official Website: https://www.collegewebsite.com</p>
                            <p>Address: Building, Campus, {student?.department || "Department"} | Baripada, Mayurbhanj, Odisha | Phone: 6732456782</p>
                        </div>

                        <div className="bill-title">College fee</div>
                        <div className="student-copy">Student Copy</div>
                        <div className="bill-content-wrapper">
                            <table className="bill-details-table">
                                <tbody>
                                    <tr>
                                        <td><strong>Receipt No.: </strong>{receiptNo}</td>
                                        <td><strong>Receipt Date: </strong>{receiptDate}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Student Name: </strong>{student?.name || "Student Name"}</td>
                                        <td><strong>RollNo/RegNo: </strong>{student?.rollNo || "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Degree/Class: </strong>{student?.class || "XII"}</td>
                                        <td>
                                            <strong>Reference No: </strong>
                                            REF: NO: {student?.rollNo || "awaya123r1672vvhs62"}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td><strong>Financial Year: </strong>{new Date().getFullYear()}</td>
                                        <td><strong>Batch: </strong>{student?.batch || "N/A"}</td>
                                    </tr>
                                </tbody>
                            </table>

                            <table className="bill-items-table">
                                <colgroup>
                                    <col style={{ width: '10%' }} />
                                    <col style={{ width: '60%' }} />
                                    <col style={{ width: '30%' }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>S.NO</th>
                                        <th>PARTICULARS</th>
                                        <th>AMOUNT</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>1</td>
                                        <td>{billDetails.title || "Hostel Fee"}</td>
                                        <td>₹ {billDetails.amount || "2000.00"}</td>
                                    </tr>
                                    {/* Reduced number of empty rows to prevent excess space */}
                                    {[...Array(3)].map((_, index) => (
                                        <tr key={index}>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <td colSpan="2" className="bill-total">Total</td>
                                        <td className="bill-amount">₹ {billDetails.amount || "2000.00"}</td>
                                    </tr>
                                </tbody>
                            </table>

                            <table className="bill-footer-table">
                                <tbody>
                                    <tr>
                                        <td><strong>In words: </strong>{convertNumberToWords(billDetails.amount)} Rupees Only</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Pay's Name: </strong>Cash / DEP. NO: 08558bcp66xbyqgTPP</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Bank: </strong>{paymentMethod === 'bank' ? 'National Bank' : 'N/A BANK'}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Remarks: </strong>{billDetails.remarks || "User generated remarks"}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="bill-footer">
                            <p>This is Computer Generated Receipt</p>
                            <p>Note: Please keep this receipt for future purpose in regards of fee paid</p>
                            <p>* Contact the office in case of any kind of discrepancy</p>
                        </div>
                    </div>
                </div>

                <div className="bill-success-message">
                    <CheckCircle size={24} className="success-icon" />
                    <div>
                        <h3>Bill Generated Successfully!</h3>
                        <p>Receipt No: {receiptNo}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Helper function to convert number to words
function convertNumberToWords(number) {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
        'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    if (!number) return '';

    number = parseFloat(number);

    if (isNaN(number)) return '';

    if (number === 0) return 'Zero';

    let words = '';

    if (Math.floor(number / 1000) > 0) {
        words += convertNumberToWords(Math.floor(number / 1000)) + ' Thousand ';
        number %= 1000;
    }

    if (Math.floor(number / 100) > 0) {
        words += ones[Math.floor(number / 100)] + ' Hundred ';
        number %= 100;
    }

    if (number > 0) {
        if (words !== '') words += 'and ';

        if (number < 20) {
            words += ones[number];
        } else {
            words += tens[Math.floor(number / 10)];
            if (number % 10 > 0) {
                words += ' ' + ones[number % 10];
            }
        }
    }

    return words;
}

export default HostelStudentBillPDF;